import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useTable } from 'react-table';
import moment from 'moment-timezone';
import semver from 'semver';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Badge, Table, Button, Tooltip, OverlayTrigger } from 'react-bootstrap';
import EcsTaskDialog from './EcsTaskDialog';
import { EcsContext } from './contexts/EcsContext';
import { authenticatedFetch } from "./authenticatedFetch";
import config from './config';

const fetchData = async (url) => {
    try {
        const response = await authenticatedFetch(url);
        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        return data;
    } catch (error) {
        console.error('Error fetching data:', error);
        return [];
    }
};

const filterImages = (images) => {
    return images
        .filter(image =>
            !image.includes('opa') &&
            !image.includes('envoy') &&
            !image.includes('xray')
        )
        .map(image => image.split(':').pop());
};

const processServiceName = (name) => {
    return name.slice(3).replace('-', '');
};

const abbreviateVersion = (version) => {
    if (!version) return 'N/A'; // Return 'N/A' if version is undefined, null, or empty string

    if (typeof version !== 'string') {
        console.warn('Unexpected version type:', typeof version, version);
        return String(version); // Convert to string if it's not already
    }

    if (version.length > 15) {
        return `${version.slice(0, 8)}...${version.slice(-6)}`;
    }
    return version;
};

const stripServiceNamePrefix = (serviceName) => {
    const parts = serviceName.split('-');
    return parts.slice(2).join('-');
};

const isSemver = (version) => {
    return semver.valid(semver.coerce(version)) !== null;
};

const EcsCluster = (props) => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [lastRefreshed, setLastRefreshed] = useState(moment());
    const [nextRefresh, setNextRefresh] = useState(moment().add(5, 'minutes'));
    const [showDialog, setShowDialog] = useState(false);
    const [dialogData, setDialogData] = useState(null);
    const [selectedVersions, setSelectedVersions] = useState([]);
    const { ecsClusters, ecsServices } = useContext(EcsContext);
    const [deploymentPlan, setDeploymentPlan] = useState({});
    const cluster = props.cluster;

    const envOrder = ['dev', 'uat', 'xbx', 'prd', 'sso'];

    const handleDragStart = (e, version, env, serviceName) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({ version, env, serviceName }));
    };

    const handleDragOver = (e) => {
        e.preventDefault();
    };

    const handleDrop = (e, targetEnv, serviceName) => {
        console.log('handleDrop called');
        e.preventDefault();
        e.stopPropagation();

        try {
            console.log('Drop event:', e);
            console.log('Target environment:', targetEnv);
            console.log('Service name:', serviceName);

            const draggedDataString = e.dataTransfer.getData('text/plain');
            console.log('Dragged data string:', draggedDataString);

            if (!draggedDataString) {
                console.error('No data transferred');
                return;
            }

            const draggedData = JSON.parse(draggedDataString);
            console.log('Parsed dragged data:', draggedData);

            if (!draggedData || !draggedData.serviceName || !draggedData.env || !draggedData.version) {
                console.error('Invalid dragged data:', draggedData);
                return;
            }

            if (draggedData.serviceName !== serviceName) {
                console.log('Service name mismatch');
                alert('You can only drag and drop versions within the same service.');
                return;
            }

            if (draggedData.env === targetEnv) {
                console.log('Same environment');
                alert('The version is already in this environment.');
                return;
            }

            // Find the service in the data array
            const service = data.find(s => serviceName.endsWith(s.serviceName));
            console.log('Found service:', service);

            if (!service) {
                console.error('Service not found:', serviceName);
                return;
            }

            // Get the current version in the target environment
            const currentVersion = service[targetEnv]?.imageVersion;
            console.log('Current version in target environment:', currentVersion);

            if (!currentVersion) {
                console.error('No current version found for environment:', targetEnv);
                return;
            }

            if (draggedData.version === currentVersion) {
                console.log('Version already deployed');
                alert('This version is already deployed in the target environment.');
                return;
            }

            setDeploymentPlan(prevPlan => {
                const newPlan = {
                    ...prevPlan,
                    [serviceName]: {
                        ...prevPlan[serviceName],
                        [targetEnv]: {
                            sourceEnv: draggedData.env,
                            sourceVersion: draggedData.version,
                            currentVersion: currentVersion,
                            targetVersion: draggedData.version
                        }
                    }
                };
                console.log('New deployment plan:', newPlan);
                return newPlan;
            });

            console.log('Deployment plan updated successfully');
        } catch (error) {
            console.error('Error in handleDrop:', error);
        }
    };

    const removeFromDeploymentPlan = (service, env) => {
        setDeploymentPlan(prevPlan => {
            const newPlan = { ...prevPlan };
            if (newPlan[service]) {
                delete newPlan[service][env];
                if (Object.keys(newPlan[service]).length === 0) {
                    delete newPlan[service];
                }
            }
            return newPlan;
        });
    };

    const fetchDataFromAllEnvironments = async () => {
        setLoading(true);

        const environmentEntries = Object.entries(ecsClusters[cluster]);
        const fetchPromises = environmentEntries.map(([env, url]) =>
            fetchData(config.apiUrl + `/ecs/` + env + `/clusters/` + url).then(data => ({ env, data }))
        );

        const allData = await Promise.all(fetchPromises);
        const processedData = {};

        allData.forEach(({ env, data }) => {
            data.forEach(service => {
                const serviceName = processServiceName(service.service_name);
                if (!processedData[serviceName]) {
                    processedData[serviceName] = { serviceName };
                }
                processedData[serviceName][env] = {
                    status: service.status,
                    imageVersion: filterImages(service.container_image_names).join(', '),
                    fullData: service
                };
            });
        });

        setData(Object.values(processedData));
        setLastRefreshed(moment());
        setNextRefresh(moment().add(5, 'minutes'));
        setLoading(false);
    };

    useEffect(() => {
        fetchDataFromAllEnvironments();
        const interval = setInterval(fetchDataFromAllEnvironments, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const sortedData = useMemo(() => {
        return [...data].sort((a, b) => a.serviceName.localeCompare(b.serviceName));
    }, [data]);

    const getStatusColor = (status, runningCount, desiredCount, pendingCount) => {
        if (status !== 'ACTIVE') return 'danger';
        if (runningCount === 0) return 'danger';
        if (pendingCount > 0) return 'warning';
        if (desiredCount === runningCount) return 'success';
        return 'secondary';
    };

    const formatDate = (date) => {
        const localTime = moment(date).local();
        return `${localTime.format('YYYY-MM-DD HH:mm:ss')} ${localTime.format('z')}`;
    };

    const handleVersionSelect = (version, env, serviceName) => {
        if (!isSemver(version)) return;

        setSelectedVersions(prev => {
            const existingIndex = prev.findIndex(v => v.version === version && v.env === env && v.serviceName === serviceName);
            if (existingIndex !== -1) {
                // If already selected, remove it
                return prev.filter((_, index) => index !== existingIndex);
            } else if (prev.length === 2) {
                // If two versions are already selected, replace the oldest one
                return [prev[1], { version, env, serviceName }];
            } else {
                // Otherwise, add the new selection
                return [...prev, { version, env, serviceName }];
            }
        });
    };

    const getCompareLink = () => {
        if (selectedVersions.length !== 2 || selectedVersions[0].serviceName !== selectedVersions[1].serviceName) {
            return null;
        }

        const serviceName = stripServiceNamePrefix(selectedVersions[0].serviceName);
        const repoInfo = ecsServices[serviceName];
        if (!repoInfo) return null;

        const baseUrl = `https://github.com/${repoInfo.owner}/${repoInfo.repo}`;
        const [earlierVersion, laterVersion] = selectedVersions.sort((a, b) =>
            semver.compare(semver.coerce(a.version), semver.coerce(b.version))
        );
        return {
            url: `${baseUrl}/compare/${earlierVersion.version}...${laterVersion.version}`,
            repoName: `${repoInfo.owner}/${repoInfo.repo}`
        };
    };

    const getDeploymentCompareLink = (service, currentVersion, targetVersion) => {
        console.log('Getting compare link for:', { service, currentVersion, targetVersion });

        if (!service || !currentVersion || !targetVersion) {
            console.warn('Missing required parameters for comparison link');
            return null;
        }

        const strippedServiceName = stripServiceNamePrefix(service);
        console.log('Stripped service name:', strippedServiceName);

        let repoInfo = ecsServices[strippedServiceName];

        // If not found, try looking up without stripping the prefix
        if (!repoInfo) {
            console.log('Repository info not found for stripped name, trying original name');
            repoInfo = ecsServices[service];
        }

        if (!repoInfo) {
            console.warn('Repository info not found for service:', service);
            return null;
        }

        console.log('Repository info found:', repoInfo);

        const baseUrl = `https://github.com/${repoInfo.owner}/${repoInfo.repo}`;
        const compareUrl = `${baseUrl}/compare/${currentVersion}...${targetVersion}`;

        console.log('Generated compare URL:', compareUrl);

        return compareUrl;
    };

    const clearSelection = () => {
        setSelectedVersions([]);
    };

    const columns = useMemo(
        () => {
            const envColumns = envOrder.map(env => {
                if (ecsClusters[cluster][env]) {
                    return {
                        Header: env.toUpperCase(),
                        accessor: `${env}`,
                        Cell: ({ value, row }) => {
                            if (!value) return <span>-</span>;

                            const statusColor = getStatusColor(
                                value.status,
                                value.fullData.running_count,
                                value.fullData.desired_count,
                                value.fullData.pending_count
                            );
                            const imageVersion = filterImages(value.fullData.container_image_names).join(', ');
                            const isSelected = selectedVersions.some(v => v.version === imageVersion && v.env === env && v.serviceName === row.original.serviceName);
                            const isSelectable = isSemver(imageVersion);
                            const plannedDeployment = deploymentPlan[row.original.serviceName]?.[env];

                            const handleClick = (e) => {
                                if (e.ctrlKey || e.metaKey) {
                                    handleVersionSelect(imageVersion, env, row.original.serviceName);
                                } else {
                                    setDialogData(value.fullData);
                                    setShowDialog(true);
                                }
                            };

                            return (
                                <div
                                    draggable={isSelectable}
                                    onDragStart={(e) => {
                                        e.stopPropagation();
                                        handleDragStart(e, imageVersion, env, row.original.serviceName);
                                    }}
                                    onDragOver={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                    }}
                                    onDrop={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleDrop(e, env, row.original.serviceName);
                                    }}
                                    style={{ cursor: isSelectable ? 'move' : 'pointer' }}
                                >
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip>
                                                <strong>Status:</strong> {value.status}<br/>
                                                <strong>Tasks:</strong> {value.fullData.running_count}/{value.fullData.desired_count}/{value.fullData.pending_count}<br/>
                                                <strong>Last Deployment:</strong> {formatDate(value.fullData.last_deployment)}<br/>
                                                <strong>Image:</strong> {imageVersion}<br/>
                                                <strong>Click to view details</strong><br/>
                                                {isSelectable && <strong>Ctrl+Click (Cmd+Click on Mac) to select for comparison</strong>}<br/>
                                                {isSelectable && <strong>Drag to deploy to another environment</strong>}
                                            </Tooltip>
                                        }
                                    >
                                        <Badge
                                            bg={isSelected ? 'info' : statusColor}
                                            style={{ opacity: isSelectable ? 1 : 0.6 }}
                                            onClick={handleClick}
                                        >
                                            {abbreviateVersion(imageVersion)}
                                        </Badge>
                                    </OverlayTrigger>
                                    {plannedDeployment && (
                                        <Badge bg="warning" className="ms-2">
                                            ➔ {abbreviateVersion(plannedDeployment.targetVersion)}
                                        </Badge>
                                    )}
                                </div>
                            );
                        },
                    };
                }
                return null;
            }).filter(Boolean);

            return [
                {
                    Header: 'Backend Service',
                    accessor: 'serviceName',
                    Cell: ({ value }) => {
                        const strippedServiceName = stripServiceNamePrefix(value);
                        const serviceName = stripServiceNamePrefix(value);
                        const repoInfo = ecsServices[serviceName];
                        if (repoInfo) {
                            const baseUrl = `https://github.com/${repoInfo.owner}/${repoInfo.repo}`;
                            const url = repoInfo.folder ? `${baseUrl}${repoInfo.folder}` : baseUrl;
                            return (
                                <div style={{ whiteSpace: 'nowrap' }}>
                                    <a href={url} target="_blank" rel="noopener noreferrer">{strippedServiceName}</a>
                                </div>
                            );
                        }
                        return <div style={{ whiteSpace: 'nowrap' }}>{strippedServiceName}</div>;
                    },
                },
                ...envColumns
            ];
        },
        [cluster, ecsClusters, ecsServices, selectedVersions, deploymentPlan]
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
    } = useTable({ columns, data: sortedData });

    useEffect(() => {
        const timer = setInterval(() => {
            setLastRefreshed(moment(lastRefreshed));
            setNextRefresh(moment(nextRefresh));
        }, 1000);
        return () => clearInterval(timer);
    }, [lastRefreshed, nextRefresh]);

    return (
        <div className="container-fluid mt-1">
            <h3>{props.cluster}</h3>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <Button variant="primary" onClick={fetchDataFromAllEnvironments}>Refresh</Button>
                {lastRefreshed && (
                    <div>
                        Last Refresh: {lastRefreshed.fromNow()} | Next: {nextRefresh.fromNow()}
                    </div>
                )}
            </div>

            {selectedVersions.length > 0 && (
                <div className="mb-3">
                    <h5>Selected Versions for Comparison:</h5>
                    {selectedVersions.map((v, i) => (
                        <Badge key={i} bg="info" className="me-2">
                            {v.serviceName} - {v.env}: {abbreviateVersion(v.version)}
                        </Badge>
                    ))}
                    {getCompareLink() && (
                        <div>
                            <Button
                                variant="link"
                                href={getCompareLink().url}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Compare on GitHub
                            </Button>
                            <span className="text-muted">({getCompareLink().repoName})</span>
                        </div>
                    )}
                    <Button variant="secondary" size="sm" onClick={clearSelection} className="mt-2">
                        Clear Selection
                    </Button>
                </div>
            )}

            {/* Deployment Plan section */}
            {deploymentPlan && Object.keys(deploymentPlan).length > 0 && (
                <div className="mb-3">
                    <h5>Deployment Plan:</h5>
                    <div style={{ maxWidth: '900px' }}>
                        <Table striped bordered hover size="sm">
                            <thead>
                            <tr>
                                <th>Environment</th>
                                <th>Service</th>
                                <th>Current Version</th>
                                <th>New Version</th>
                                <th>Compare</th>
                                <th>Action</th>
                            </tr>
                            </thead>
                            <tbody>
                            {envOrder.map(env => {
                                const envPlans = Object.entries(deploymentPlan).filter(([_, plans]) => plans && plans[env]);
                                return envPlans.map(([service, plans]) => {
                                    const currentVersion = plans[env]?.currentVersion;
                                    const targetVersion = plans[env]?.targetVersion;
                                    const compareLink = getDeploymentCompareLink(service, currentVersion, targetVersion);
                                    return (
                                        <tr key={`${env}-${service}`}>
                                            <td>{env.toLowerCase()}</td>
                                            <td>{service}</td>
                                            <td>{abbreviateVersion(currentVersion)}</td>
                                            <td>{abbreviateVersion(targetVersion)}</td>
                                            <td>
                                                {compareLink ? (
                                                    <Button
                                                        variant="link"
                                                        size="sm"
                                                        href={compareLink}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                    >
                                                        Compare
                                                    </Button>
                                                ) : (
                                                    <span title="Compare link not available">N/A</span>
                                                )}
                                            </td>
                                            <td>
                                                <Button
                                                    variant="danger"
                                                    size="sm"
                                                    onClick={() => removeFromDeploymentPlan(service, env)}
                                                >
                                                    Remove
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                });
                            })}
                            </tbody>
                        </Table>
                    </div>
                    <div className="mt-2">
                        <Button variant="primary" size="sm" onClick={() => console.log(deploymentPlan)}>
                            Create Deployment Plan
                        </Button>
                    </div>
                </div>
            )}

            {/* Table rendering */}
            {loading ? (
                <div className="spinner-grow text-secondary" role="status">
                    <span className="sr-only"></span>
                </div>
            ) : (
                <div className="table-responsive">
                    <Table striped bordered hover size="sm" {...getTableProps()}>
                        <thead>
                        {headerGroups.map(headerGroup => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map(column => (
                                    <th {...column.getHeaderProps()}>
                                        {column.render('Header')}
                                    </th>
                                ))}
                            </tr>
                        ))}
                        </thead>
                        <tbody {...getTableBodyProps()}>
                        {rows.map(row => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map(cell => (
                                        <td {...cell.getCellProps()}>
                                            {cell.render('Cell')}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                        </tbody>
                    </Table>
                </div>
            )}

            {/* EcsTaskDialog */}
            {showDialog && (
                <EcsTaskDialog
                    show={showDialog}
                    onHide={() => setShowDialog(false)}
                    taskData={dialogData}
                />
            )}
        </div>
    );
};

export default EcsCluster;